import React, { ChangeEventHandler, FunctionComponent, MouseEventHandler, useContext, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Input,
  Spacer,
  Stack,
  Text
} from '@chakra-ui/react';
import { IoFemale, IoMale } from 'react-icons/io5';
import { Reducer, useImmerReducer } from 'use-immer';
import { useReward } from 'react-rewards';
import { ButtonRadioGroup } from '../chakraUIPro/ButtonRadioGroup';
import { Steps } from '../chakraUIPro/Steps';
import { StepContent } from '../chakraUIPro/StepContent';
import { useSteps } from '../chakraUIPro/useSteps';
import { Step } from '../chakraUIPro/Step';
import Profile, { getProduct } from '../Profile';
import { ShopDispatchContext } from '../../contexts/ShopContext';
import { ToggleButtonGroup } from '../chakraUIPro/ToggleButtonGroup';
import { ToggleButton } from '../chakraUIPro/ToggleButton';
import {
  activityOptions,
  adultOptions,
  ageOptions,
  childOptions,
  dietOptions,
  Profile as ProfileType,
  weightOptions
} from '../../typings';
import { ColorPicker } from '../chakraUIPro/ColorPicker/ColorPicker';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import Plant from './plant';

export const colours = {
  childMale: [{ label: 'Blue', color: '#347CB0' }],
  childFemale: [{ label: 'Pink', color: '#B64A7D' }],
  male: [
    { label: 'Vitamin orange', color: '#ff9016' },
    { label: 'Shiraz red', color: '#c20f2f' },
    { label: 'Jade green', color: '#00b34f' },
    { label: 'Denim blue', color: '#0f6fb5' }
  ],
  female: [
    { label: 'Amethyst purple', color: '#a05bbf' },
    { label: 'Bondi blue', color: '#0098a7' },
    { label: 'Lipstick pink', color: '#a60364' },
    { label: 'Cold purple', color: '#b3b3e0' }
  ]
};

export const getColourOptions = ({ sex, age }: ProfileType) => {
  if (!sex) {
    return [];
  }

  if (age === 'child' && sex === 'male') {
    return colours.childMale;
  }

  if (age === 'child' && sex === 'female') {
    return colours.childFemale;
  }

  return colours[sex];
};

interface QuizProps {
  existingValues?: {
    nickname: string;
    sex: string;
    age: string;
    diet: string;
    weight: string;
    activity: string;
    colour: string;
  };
  hideStepsImage?: boolean;
  onUpdate?: () => void;
  onCreate?: (profile?: ProfileType) => void;
  formType?: 'Add profile' | 'Update profile' | 'Upgrade subscription';
  subscriptionId?: string;
}

const initialValues: ProfileType = {
  nickname: '',
  sex: undefined,
  age: undefined,
  diet: undefined,
  weight: undefined,
  activity: undefined,
  colour: undefined,
  error: undefined
};
const quizReducer: Reducer = (draft, action) => {
  switch (action.type) {
    case 'updateField': {
      draft[action.field] = action.value;
      break;
    }
    case 'error': {
      draft.error = action.error;
      break;
    }
    case 'reset': {
      draft.nickname = '';
      draft.sex = '';
      draft.age = '';
      draft.diet = '';
      draft.weight = '';
      draft.activity = '';
      draft.colour = '';
      draft.error = undefined;
      break;
    }
    default:
      break;
  }
};

const ConfettiConfig = {
  elementCount: 100,
  colors: ['#FE7820', '#F45264', '#C44F8D', '#805797', '#445580', '#2F4858', '#FFFFFF']
};

const Quiz: FunctionComponent<QuizProps> = ({
  existingValues,
  hideStepsImage,
  onUpdate,
  formType,
  onCreate,
  subscriptionId
}) => {
  const { logEvent } = useAnalytics();
  const { nextStep, prevStep, activeStep, reset } = useSteps({ initialStep: 0 });
  const { addProfile, updateProfile, upgradeSubscription } = useContext(ShopDispatchContext);
  const [state, dispatch] = useImmerReducer(quizReducer, existingValues || initialValues);
  const { nickname, age, sex, diet, weight, activity, colour, error } = state;
  const [loading, setLoading] = useState<boolean>(false);
  const { reward } = useReward('rewardId', 'confetti', ConfettiConfig);

  const handleClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = async () => {
    setLoading(true);
    dispatch({ type: 'error', error: undefined });

    const { profileCombination } = getProduct(state);
    try {
      await addProfile({ ...state, profileCombination });

      logEvent({
        action: 'add_profile',
        category: 'Quiz',
        label: `Profile combination ${profileCombination}`
      });

      setLoading(false);
      dispatch({ type: 'reset' });
      reset();
    } catch (err: any) {
      dispatch({ type: 'error', error: err.message });
      setLoading(false);
    }
  };

  const handleUpgradeSubscription: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = async () => {
    setLoading(true);
    dispatch({ type: 'error', error: undefined });

    const { profileCombination } = getProduct(state);
    try {
      if (!subscriptionId) {
        throw new Error('No subscription ID provided');
      }

      const newProfile = await upgradeSubscription(
        { ...state, profileCombination: getProduct(state).profileCombination },
        subscriptionId
      );

      if (onCreate) {
        onCreate(newProfile);
      }

      logEvent({
        action: 'upgrade_subscription',
        category: 'Quiz',
        label: `Profile combination ${profileCombination}`
      });

      setLoading(false);
      dispatch({ type: 'reset' });
      reset();
    } catch (err: any) {
      dispatch({ type: 'error', error: err.message });
      setLoading(false);
    }
  };
  const handleUpdateClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = async () => {
    setLoading(true);
    dispatch({ type: 'error', error: undefined });

    try {
      await updateProfile({ ...state, profileCombination: getProduct(state).profileCombination });

      if (onUpdate) {
        onUpdate();
      }

      setLoading(false);
      dispatch({ type: 'reset' });
      reset();
    } catch (err: any) {
      dispatch({ type: 'error', error: err.message });
      setLoading(false);
    }
  };
  const handleNicknameChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    dispatch({ type: 'updateField', field: e.target.name, value: e.target.value });
  };
  let newAgeOptions = ageOptions;
  // set ageOption according to account details
  if (existingValues) {
    if (existingValues.age !== 'child') {
      newAgeOptions = adultOptions;
    }
    if (existingValues.age === 'child') {
      newAgeOptions = childOptions;
    }
  }
  return (
    <Grid templateColumns='repeat(4, 1fr)' mx='auto' maxW='6xl'>
      {!hideStepsImage && (
        <Flex
          alignItems='center'
          justifyContent='center'
          display={{ base: 'none', md: 'block' }}
          py={{ base: 12, md: 20 }}
        >
          <Flex flexDir='row' alignItems='flex-end' justifyContent='center' id='rewardId'>
            <Box boxSize='180px'>
              <Plant stepNum={activeStep + 1} />
            </Box>
          </Flex>
        </Flex>
      )}
      <GridItem
        colSpan={{ base: 4, md: hideStepsImage ? 4 : 3 }}
        py={{ base: hideStepsImage ? 4 : 12, md: hideStepsImage ? 8 : 20 }}
        px={{ base: 2, md: 8 }}
      >
        <Steps activeStep={activeStep}>
          <Step title='Nicknames'>
            <StepContent>
              <Stack shouldWrapChildren spacing='4'>
                <Text>We want each vitamin subscription to be personalised. It's for you, or someone you love.</Text>
                <Box maxW={{ base: 'xl', md: '7xl' }}>
                  <Box maxW='l'>
                    <Input
                      placeholder='A nickname'
                      name='nickname'
                      focusBorderColor='orange.300'
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          nextStep();
                        }
                      }}
                      value={nickname}
                      onChange={handleNicknameChange}
                      autoComplete='nickname'
                    />
                  </Box>
                </Box>

                <Button isDisabled={!nickname} onClick={nextStep}>
                  Next
                </Button>
              </Stack>
            </StepContent>
          </Step>
          <Step title='Male or Female'>
            <StepContent>
              <Stack shouldWrapChildren spacing='4'>
                <Text>
                  {`We need to know to find the perfect vitamin combination for your sex.
                    A person's sex has a big impact on your vitamin requirements`}
                </Text>
                <Box maxW={{ base: 'xl', md: '7xl' }} mx='auto' px={{ base: 0, md: '8' }}>
                  <Box maxW='l' mx='auto'>
                    <ButtonRadioGroup
                      value={sex}
                      onChange={(selected) => {
                        dispatch({ type: 'updateField', field: 'sex', value: selected });
                        nextStep();
                      }}
                      options={[
                        {
                          label: 'Male',
                          description: 'My birth certificate says I am Male',
                          icon: <IoMale />,
                          value: 'male'
                        },
                        {
                          label: 'Female',
                          description: 'My birth certificate says I am Female',
                          icon: <IoFemale />,
                          value: 'female'
                        }
                      ]}
                    />
                  </Box>
                </Box>
                <HStack>
                  <Button onClick={prevStep} variant='ghost' color='gray.500'>
                    Back
                  </Button>
                  <Button isDisabled={!sex} onClick={nextStep}>
                    Next
                  </Button>
                </HStack>
              </Stack>
            </StepContent>
          </Step>
          <Step title="What's the age again?">
            <StepContent>
              <Stack shouldWrapChildren spacing='4'>
                <Text>Your daily vitamin needs tend to change as you grow older.</Text>
                <Flex justify='left' py={{ base: 4, sm: 6, md: 8 }} maxW='5xl' mx='auto'>
                  <ToggleButtonGroup
                    size='lg'
                    value={existingValues ? existingValues.age : age}
                    onChange={(value) => {
                      dispatch({ value, type: 'updateField', field: 'age' });
                      nextStep();
                    }}
                    variant='outline'
                    aria-label='Age options'
                    flexWrap={['wrap', 'wrap', 'nowrap']}
                  >
                    {Object.keys(newAgeOptions).map((key) => (
                      <ToggleButton value={key} aria-label={ageOptions[key]} key={key}>
                        {ageOptions[key]}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Flex>
                <HStack>
                  <Button onClick={prevStep} variant='ghost' color='gray.500'>
                    Back
                  </Button>
                  <Button isDisabled={!age} onClick={nextStep}>
                    Next
                  </Button>
                </HStack>
              </Stack>
            </StepContent>
          </Step>
          <Step title='The weight question'>
            <StepContent>
              <Stack shouldWrapChildren spacing='4'>
                <Text>We know this could be sensitive. But, we have to ask.</Text>
                <Box py={{ base: 4, sm: 6, md: 8 }} maxW='5xl' mx='auto'>
                  <ToggleButtonGroup
                    size='lg'
                    value={weight}
                    onChange={(value) => {
                      dispatch({ value, type: 'updateField', field: 'weight' });
                      nextStep();
                    }}
                    variant='outline'
                    aria-label='Weight options'
                    flexWrap={['wrap', 'wrap', 'nowrap']}
                  >
                    {Object.keys(weightOptions).map((key) => (
                      <ToggleButton value={key} aria-label={weightOptions[key]} key={key}>
                        {weightOptions[key]}
                      </ToggleButton>
                    ))}
                    <Spacer />
                  </ToggleButtonGroup>
                </Box>
                <HStack>
                  <Button onClick={prevStep} variant='ghost' color='gray.500'>
                    Back
                  </Button>
                  <Button isDisabled={!weight} onClick={nextStep}>
                    Next
                  </Button>
                </HStack>
              </Stack>
            </StepContent>
          </Step>
          <Step title='How active is active?'>
            <StepContent>
              <Stack shouldWrapChildren spacing='4'>
                <Text>Are you stuck behind a desk all day or do you get a chance to get out?</Text>
                <Flex justify='left' py={{ base: 4, sm: 6, md: 8 }} maxW='5xl' mx='auto'>
                  <ToggleButtonGroup
                    size='lg'
                    value={activity}
                    onChange={(value) => {
                      dispatch({ value, type: 'updateField', field: 'activity' });
                      nextStep();
                    }}
                    variant='outline'
                    aria-label='Activity options'
                    flexWrap={['wrap', 'wrap', 'nowrap']}
                  >
                    {Object.keys(activityOptions).map((key) => (
                      <ToggleButton value={key} aria-label={activityOptions[key]} key={key}>
                        {activityOptions[key]}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Flex>
                <HStack>
                  <Button onClick={prevStep} variant='ghost' color='gray.500'>
                    Back
                  </Button>
                  <Button isDisabled={!activity} onClick={nextStep}>
                    Next
                  </Button>
                </HStack>
              </Stack>
            </StepContent>
          </Step>
          <Step title="How's the diet?">
            <StepContent>
              <Stack shouldWrapChildren spacing='4'>
                <Text>It's important to get a balanced vitamin intake.</Text>
                <Flex justify='left' py={{ base: 4, sm: 6, md: 8 }} maxW='5xl' mx='auto'>
                  <ToggleButtonGroup
                    size='lg'
                    value={diet}
                    onChange={(value) => {
                      dispatch({ value, type: 'updateField', field: 'diet' });
                      nextStep();
                    }}
                    variant='outline'
                    aria-label='Diet options'
                    flexWrap={['wrap', 'wrap', 'nowrap']}
                    justifyItems='space-between'
                  >
                    {Object.keys(dietOptions).map((key) => (
                      <ToggleButton value={key} aria-label={dietOptions[key]} key={key}>
                        {dietOptions[key]}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Flex>
                <HStack>
                  <Button onClick={prevStep} variant='ghost' color='gray.500'>
                    Back
                  </Button>
                  <Button isDisabled={!diet} onClick={nextStep}>
                    Next
                  </Button>
                </HStack>
              </Stack>
            </StepContent>
          </Step>
          <Step title='What is your favourite colour?'>
            <StepContent>
              <Stack shouldWrapChildren spacing='4'>
                <Text>Get your vitamins dispensed in your favourite colour</Text>
                <Flex justify='left' py={{ base: 4, sm: 6, md: 8 }} maxW='5xl' mx='auto'>
                  <ColorPicker
                    // value={getColourOptions({ age, sex })[0].label}
                    value={colour || existingValues?.colour}
                    options={getColourOptions({ age, sex })}
                    onChange={(value) => {
                      dispatch({ value, type: 'updateField', field: 'colour' });
                      nextStep();
                      reward();
                    }}
                  />
                </Flex>
                <HStack>
                  <Button onClick={prevStep} variant='ghost' color='gray.500'>
                    Back
                  </Button>
                  <Button isDisabled={!colour} onClick={nextStep}>
                    Next
                  </Button>
                </HStack>
              </Stack>
            </StepContent>
          </Step>
          <Step title='Our recommendation'>
            <StepContent>
              <Stack shouldWrapChildren spacing='4'>
                <Text>
                  After crunching some numbers, we recommend the following vitamins and quantities for your daily
                  vitamin regimen.
                </Text>
                {sex && age && diet && <Profile profile={state} />}
                {/* <div><pre>{JSON.stringify(products[0].variants[0].id, null, 2)}</pre></div> */}
                <HStack width='full'>
                  <Button onClick={prevStep} variant='ghost' color='gray.500'>
                    Back
                  </Button>
                  {!existingValues && formType !== 'Upgrade subscription' && (
                    <Button
                      minW={200}
                      isLoading={loading}
                      loadingText='Adding your profile'
                      colorScheme='orange'
                      onClick={handleClick}
                    >
                      Add this Vitamin Profile
                    </Button>
                  )}
                  {existingValues && (
                    <Button
                      minW={200}
                      isLoading={loading}
                      loadingText='Updating your profile'
                      colorScheme='orange'
                      onClick={handleUpdateClick}
                    >
                      Update this Vitamin Profile
                    </Button>
                  )}
                  {formType === 'Upgrade subscription' && (
                    <Button
                      minW={200}
                      isLoading={loading}
                      loadingText='Adding profile'
                      colorScheme='orange'
                      onClick={handleUpgradeSubscription}
                    >
                      Add profile to subscription
                    </Button>
                  )}
                </HStack>
                {error && (
                  <Alert status='error' borderRadius='md'>
                    <AlertIcon />
                    <Box>
                      <AlertTitle fontWeight='bolder'>Unable to add profile at this time</AlertTitle>
                      <AlertDescription>Please check your connection and try again.</AlertDescription>
                    </Box>
                  </Alert>
                )}
              </Stack>
            </StepContent>
          </Step>
        </Steps>
      </GridItem>
    </Grid>
  );
};

export default Quiz;
